import { useMemo } from "react";
import { IPageRoute, IRoute } from "../../../routes";
import { usePermissions } from "../../../contexts/PermissionContext";
import { useShortcutIds } from "./shortcutStore";

export interface NavPage {
  id: string;
  label: string;
  link: string;
  groupId: string;
  groupLabel: string;
}

export interface NavGroup {
  id: string;
  label: string;
  pages: NavPage[];
}

/** Group order and membership come straight from the API-filtered routes. */
function buildNavGroups(routes: IRoute[]): NavGroup[] {
  return routes
    .map((group) => ({
      id: group.id,
      label: group.label,
      pages: (group.subpages ?? [])
        .filter((page): page is IPageRoute => Boolean(page.link))
        .map((page) => ({
          id: page.id,
          label: page.label,
          link: page.link,
          groupId: group.id,
          groupLabel: group.label,
        })),
    }))
    .filter((group) => group.pages.length > 0);
}

/** Subsequence match; prefix and word-start hits outrank scattered letters. */
export function scoreMatch(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();

  const direct = t.indexOf(q);
  if (direct === 0) return 1000;
  if (direct > 0) return t[direct - 1] === " " ? 800 : 600;

  // Fall back to a subsequence walk so "lgw" still finds "Live Gateway".
  let ti = 0;
  let score = 0;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found === -1) return -1;
    score += found === 0 || t[found - 1] === " " ? 12 : 4;
    ti = found + 1;
  }
  return score;
}

export interface NavSearchHit extends NavPage {
  score: number;
}

/** Ranked page hits for a query, matched against both page and group label. */
export function searchPages(query: string, groups: NavGroup[]): NavSearchHit[] {
  if (!query.trim()) return [];
  return groups
    .flatMap((group) => group.pages)
    .map((page) => {
      const own = scoreMatch(query, page.label);
      // A group hit counts, but weaker than matching the page itself.
      const viaGroup = scoreMatch(query, page.groupLabel);
      const score = Math.max(own, viaGroup > 0 ? viaGroup * 0.4 : viaGroup);
      return { ...page, score };
    })
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .slice(0, 12);
}

/** Groups + the pinned shortcut pages the current user is actually allowed to see. */
export function useNavModel() {
  const { filteredRoutes, isLoading } = usePermissions();

  const groups = useMemo(
    () => buildNavGroups(filteredRoutes),
    [filteredRoutes],
  );

  const pagesById = useMemo(() => {
    const map = new Map<string, NavPage>();
    groups.forEach((group) =>
      group.pages.forEach((page) => map.set(page.id, page)),
    );
    return map;
  }, [groups]);

  const shortcutIds = useShortcutIds();

  // Pinned ids the user can no longer reach (permission change, removed route)
  // are dropped from the row but left in storage, so regaining access restores them.
  const shortcuts = useMemo(
    () =>
      shortcutIds
        .map((id) => pagesById.get(id))
        .filter((page): page is NavPage => Boolean(page)),
    [shortcutIds, pagesById],
  );

  return { groups, shortcuts, shortcutIds, pagesById, isLoading };
}

/** The group owning `pathname`, or null when the route is not in the nav. */
export function findActiveGroupId(
  groups: NavGroup[],
  pathname: string,
): string | null {
  for (const group of groups) {
    if (group.pages.some((page) => page.link === pathname)) return group.id;
  }
  return null;
}
